<template>
    <div class="data-page">
        <PageHeader v-if="!player" title="Transações" :subtitle="summaryText" />

        <div class="data-card">
            <div class="data-filters">
                <div class="data-filters-main">
                    <div class="filter-field filter-period">
                        <label>Período</label>
                        <div class="filter-period-inputs">
                            <CalendarForm pickerRange v-model="stateCalendarFilter.datesInterval"
                                :maxDate="new Date()" placeholder="Intervalo de datas" />
                        </div>
                    </div>

                    <div class="data-filters-actions">
                        <Button class="p-button-outlined" type="button"
                            :icon="state.showFilters ? 'pi pi-chevron-up' : 'pi pi-sliders-h'"
                            :label="activeFilters.length ? `Filtros (${activeFilters.length})` : 'Filtros'"
                            @click="state.showFilters = !state.showFilters" />
                        <Button type="button" icon="pi pi-search" label="Pesquisar" :loading="state.loading.transactions"
                            @click="applyFilters" />
                    </div>
                </div>

                <div v-show="state.showFilters" class="data-filters-grid">
                    <div class="filter-field">
                        <label>ID</label>
                        <InputText v-model="state.filter.id" @keyup.enter="applyFilters" />
                    </div>

                    <div class="filter-field">
                        <label>Status</label>
                        <Dropdown showClear v-model="state.filter.status" optionLabel="name" :options="statusOptions"
                            placeholder="Todos" />
                    </div>

                    <div class="filter-field">
                        <label>Tipo</label>
                        <Dropdown showClear v-model="state.filter.type" optionLabel="name" :options="typesOptions"
                            placeholder="Todos" />
                    </div>

                    <div v-if="!player" class="filter-field">
                        <label>Cliente</label>
                        <MultiSelect v-model="state.filter.clients" optionLabel="name" dataKey="id" filter
                            :options="stateClients.clients" :loading="stateClients.loading.clients"
                            :maxSelectedLabels="2" selectedItemsLabel="{0} clientes" placeholder="Todos" />
                    </div>

                    <div class="filter-field">
                        <label>Fornecedor</label>
                        <MultiSelect v-model="state.filter.vendors" optionLabel="name" dataKey="id" filter
                            :options="stateVendors.vendors" :loading="stateVendors.loading.vendors"
                            :maxSelectedLabels="2" selectedItemsLabel="{0} fornecedores" placeholder="Todos" />
                    </div>

                    <div class="filter-field">
                        <label for="filter-value">Valor</label>
                        <input id="filter-value" class="p-inputtext" type="text" inputmode="numeric"
                            placeholder="R$ 0,00" :value="valueFilterDisplay" @input="onValueFilterInput"
                            @keyup.enter="applyFilters" />
                    </div>
                </div>

                <div v-if="activeFilters.length" class="data-filters-chips">
                    <span v-for="chip in activeFilters" :key="chip.key" class="filter-chip">
                        <span class="filter-chip-label">{{ chip.label }}:</span> {{ chip.value }}
                        <button type="button" class="filter-chip-clear" :aria-label="`Remover filtro ${chip.label}`"
                            @click="clearFilter(chip.key)">
                            <i class="pi pi-times"></i>
                        </button>
                    </span>
                    <Button class="p-button-text p-button-sm" type="button" icon="pi pi-filter-slash"
                        label="Limpar filtros" @click="clearAllFilters" />
                </div>
            </div>

            <DataTable class="data-table" lazy @page="onPage" :first="state.first" :value="state.transactions"
                :loading="state.loading.transactions" paginator responsiveLayout="scroll"
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                :rows="state.perPage" :totalRecords="state.totalRecords" :rowsPerPageOptions="[20, 50, 100]">

                <template #empty>
                    <div class="data-empty">
                        <i class="pi pi-inbox"></i>
                        <span>{{ activeFilters.length ? 'Nenhuma transação para os filtros aplicados.' : 'Nenhuma transação encontrada.' }}</span>
                    </div>
                </template>

                <Column field="id" header="ID" :footer="'TOTAL'">
                    <template #body="slotProps">
                        <span class="record-id">{{ slotProps.data.id }}</span>
                    </template>
                </Column>

                <Column field="client" header="Cliente">
                    <template #body="slotProps">
                        <span v-if="slotProps.data.clientName" class="record-name">{{ slotProps.data.clientName }}</span>
                        <span v-else class="empty-value">—</span>
                    </template>
                </Column>
                <Column field="vendor" header="Fornecedor">
                    <template #body="slotProps">
                        <span v-if="slotProps.data.vendorName" class="pill pill-info">{{ slotProps.data.vendorName }}</span>
                        <span v-else class="empty-value">—</span>
                    </template>
                </Column>
                <Column field="playerName" header="Nome">
                    <template #body="slotProps">
                        <span v-if="slotProps.data.player.name">{{ slotProps.data.player.name }}</span>
                        <span v-else class="empty-value">—</span>
                    </template>
                </Column>
                <Column field="playerEmail" header="Login/Email">
                    <template #body="slotProps">
                        <span v-if="slotProps.data.player.email" class="mono-chip" :title="slotProps.data.player.email">{{ slotProps.data.player.email }}</span>
                        <span v-else class="empty-value">—</span>
                    </template>
                </Column>
                <Column field="type" header="Tipo">
                    <template #body="slotProps">
                        <span class="pill pill-status" :class="typeTone(slotProps.data.type)">
                            {{ typesLabels[slotProps.data.type] }}
                        </span>
                    </template>
                </Column>
                <Column field="amount" header="Valor" class="col-numeric" :footer="sumField('amount')">
                    <template #body="slotProps">
                        <span class="cell-amount">{{ formatAmountToCurrency(slotProps.data.amount) }}</span>
                    </template>
                </Column>
                <Column field="tax" header="Taxa" class="col-numeric" :footer="sumField('tax')">
                    <template #body="slotProps">
                        {{ formatAmountToCurrency(slotProps.data.tax) }}
                    </template>
                </Column>
                <Column field="status" header="Status">
                    <template #body="slotProps">
                        <span class="pill pill-status" :class="statusTone(slotProps.data.status)">
                            <span class="pill-dot"></span>
                            {{ statusLabels[slotProps.data.status] }}
                        </span>
                    </template>
                </Column>
                <Column field="createdAt" header="Gerado em">
                    <template #body="slotProps">
                        <span class="cell-date">{{ formatDate(slotProps.data.createdAt) }}</span>
                    </template>
                </Column>
                <Column field="fallback" header="Fallback">
                    <template #body="slotProps">
                        <span v-if="slotProps.data.fallback == 1 || slotProps.data.fallback"
                            class="pill pill-status is-warn">Sim</span>
                        <span v-else class="empty-value">Não</span>
                    </template>
                </Column>

                <Column class="col-actions">
                    <template #header>
                        Ações
                    </template>
                    <template #body="slotProps">
                        <div class="transaction-row-actions">
                            <span v-if="slotProps.data.clientNotificationAlert"
                                class="transaction-notification-alert"
                                :class="slotProps.data.clientNotificationAlert === 'ERROR' ? 'is-error' : 'is-pending'"
                                role="img" tabindex="0"
                                :aria-label="slotProps.data.clientNotificationAlert === 'ERROR'
                                    ? 'Notificação ao cliente com erro'
                                    : 'Notificação ao cliente pendente'"
                                v-tooltip.left="slotProps.data.clientNotificationAlert === 'ERROR'
                                    ? 'A notificação ao cliente falhou. Abra os detalhes para consultar ou reenviar.'
                                    : 'A notificação ao cliente está pendente.'">
                                <i :class="slotProps.data.clientNotificationAlert === 'ERROR'
                                    ? 'pi pi-exclamation-triangle'
                                    : 'pi pi-clock'"></i>
                            </span>
                            <Button class="table-action-button" type="button" icon="pi pi-eye"
                                @click="openDetailsModal(slotProps.data)"
                                v-tooltip.left="{ value: 'Vizualizar Detalhes', showDelay: 1000, hideDelay: 300 }" />
                        </div>
                    </template>
                </Column>

                <template #paginatorstart>
                    <TableResults :shown="state.transactions.length" :total="state.totalRecords"
                        :pageTotal="state.transactions.length" />
                </template>
            </DataTable>
        </div>

        <Dialog class="data-dialog" v-model:visible="state.isDetailsModalOpen" modal :style="{ width: '64rem' }"
            :breakpoints="{ '1200px': '92vw', '640px': '96vw' }" header="Detalhes da transação"
            @update:visible="handleClose">

            <div v-if="state.loading.transactionsDetails" class="data-form-loading">
                <ProgressSpinner />
            </div>

            <div v-else class="data-detail-body">

                <div class="data-dialog-subject">
                    <span class="pill pill-muted">ID {{ state.modalTransaction.id }}</span>
                    <span class="pill pill-status" :class="typeTone(state.modalTransaction.type)">
                        {{ typesLabels[state.modalTransaction.type] }}
                    </span>
                    <span class="pill pill-status" :class="statusTone(state.modalTransaction.status)">
                        <span class="pill-dot"></span>
                        {{ statusLabels[state.modalTransaction.status] }}
                    </span>
                </div>

                <div class="data-detail-grid">
                    <div class="data-detail-item">
                        <span class="data-detail-label">Valor</span>
                        <span class="data-detail-value">{{ formatAmountToCurrency(state.modalTransaction.value) }}</span>
                    </div>

                    <div class="data-detail-item">
                        <span class="data-detail-label">Taxa</span>
                        <span class="data-detail-value">{{ formatAmountToCurrency(state.modalTransaction.tax_applied_value) }}</span>
                    </div>

                    <div class="data-detail-item">
                        <span class="data-detail-label">
                            {{ checkIfTransactionTypeIs('REMOVAL', state.modalTransaction) ? 'Origem' : 'Cliente' }}
                        </span>
                        <span class="data-detail-value">
                            {{ checkIfTransactionTypeIs('REMOVAL', state.modalTransaction) ? 'Retirada pelo painel' : (state.clientName || '—') }}
                        </span>
                    </div>

                    <div class="data-detail-item">
                        <span class="data-detail-label">Fornecedor</span>
                        <span class="data-detail-value">{{ state.vendorName || '—' }}</span>
                    </div>

                    <div class="data-detail-item">
                        <span class="data-detail-label">Gerado em</span>
                        <span class="data-detail-value">{{ formatDateOrDash(state.modalTransaction.created_at) }}</span>
                    </div>

                    <div class="data-detail-item">
                        <span class="data-detail-label">Pago em</span>
                        <span class="data-detail-value">{{ formatDateOrDash(state.modalTransaction.paid_at) }}</span>
                    </div>

                    <div class="data-detail-item">
                        <span class="data-detail-label">Fallback</span>
                        <span class="data-detail-value">{{ isFallback ? 'Sim' : 'Não' }}</span>
                    </div>

                    <div v-if="isFallback" class="data-detail-item">
                        <span class="data-detail-label">Primeira opção de fornecedor</span>
                        <span class="data-detail-value">{{ state.modalTransaction.first_vendor_option || '—' }}</span>
                    </div>
                </div>

                <div v-if="state.modalTransaction.key_pix || state.modalTransaction.key_pix_type" class="data-detail-section">
                    <span class="data-detail-section-title">Chave PIX</span>
                    <div class="data-detail-grid">
                        <div class="data-detail-item">
                            <span class="data-detail-label">Tipo</span>
                            <span class="data-detail-value">{{ pixKeyTypesLabels[state.modalTransaction.key_pix_type] || '—' }}</span>
                        </div>

                        <div class="data-detail-item">
                            <span class="data-detail-label">Chave</span>
                            <span class="data-detail-value mono">{{ state.modalTransaction.key_pix || '—' }}</span>
                        </div>
                    </div>
                </div>

                <div class="data-detail-section">
                    <span class="data-detail-section-title">
                        {{ checkIfTransactionTypeIs('REMOVAL', state.modalTransaction) ? 'Recebedor' : 'Jogador' }}
                    </span>

                    <div v-if="state.player" class="data-detail-grid">
                        <div class="data-detail-item">
                            <span class="data-detail-label">Nome</span>
                            <span class="data-detail-value">{{ state.player.name || '—' }}</span>
                        </div>

                        <div class="data-detail-item">
                            <span class="data-detail-label">CPF/CNPJ</span>
                            <span class="data-detail-value mono">{{ state.player.cpf || '—' }}</span>
                        </div>

                        <div class="data-detail-item">
                            <span class="data-detail-label">Login/Email</span>
                            <span class="data-detail-value mono">{{ state.player.email || '—' }}</span>
                        </div>

                        <div class="data-detail-item">
                            <span class="data-detail-label">Code</span>
                            <span class="data-detail-value mono">{{ state.player.code || '—' }}</span>
                        </div>

                        <div class="data-detail-item">
                            <span class="data-detail-label">Status</span>
                            <span class="pill pill-status" :class="state.player.status == 'BLOCKED' ? 'is-off' : 'is-on'">
                                <span class="pill-dot"></span>
                                {{ changeStatusBoleanToText(state.player.status) }}
                            </span>
                        </div>
                    </div>

                    <span v-else class="empty-value">Nenhum dado disponível.</span>
                </div>

                <div v-if="checkIfTransactionTypeIs('DEPOSIT', state.modalTransaction) && state.modalTransaction.qr_code_image"
                    class="data-detail-section">
                    <span class="data-detail-section-title">QR Code</span>
                    <div class="data-detail-qr">
                        <img :src="formatQRCode(state.modalTransaction.qr_code_image)" width="200" height="200"
                            alt="QR Code do depósito" />
                    </div>
                </div>

                <div v-if="state.metagratorApproval.visible" class="data-detail-section">
                    <span class="data-detail-section-title">Aprovação no fornecedor</span>

                    <div class="data-detail-grid">
                        <div class="data-detail-item">
                            <span class="data-detail-label">Status no Metagrator</span>
                            <span class="data-detail-value">
                                {{ state.metagratorApproval.status || 'consultando...' }}
                            </span>
                        </div>
                    </div>

                    <span v-if="state.metagratorApproval.done" class="data-detail-hint">
                        Ação enviada ao fornecedor. Aguardando o retorno dele.
                    </span>

                    <template v-else>
                        <span v-if="state.metagratorApproval.error" class="data-detail-hint is-warn">
                            Não foi possível confirmar o status no fornecedor:
                            {{ state.metagratorApproval.error }}
                        </span>

                        <span v-else-if="state.metagratorApproval.status && !state.metagratorApproval.waiting"
                            class="data-detail-hint is-warn">
                            O fornecedor não reporta este saque como aguardando aprovação. Se ele já
                            processou, a ação abaixo volta recusada.
                        </span>

                        <div class="data-detail-actions">
                            <Button label="Aprovar saque" icon="pi pi-check"
                                :loading="state.loading.metagratorApproval"
                                @click="handleMetagratorApproval('proceed')" />

                            <Button label="Reprovar saque" icon="pi pi-times" class="p-button-danger"
                                :loading="state.loading.metagratorApproval"
                                @click="handleMetagratorApproval('rejected')" />
                        </div>

                        <span class="data-detail-hint">
                            Reprovar dispara o estorno automático no fornecedor.
                        </span>
                    </template>
                </div>

                <!-- pagamento já resolvido não mostra nada aqui: o desfecho e os retornos de
                     gateway posteriores ficam no histórico logo abaixo -->
                <div v-if="manualStatusSectionVisible" class="data-detail-section">
                    <span class="data-detail-section-title">Alteração manual de status</span>

                    <div class="data-detail-actions">
                        <Button v-for="option in manualStatusOptions" :key="option.value"
                            :label="`Marcar como ${option.name}`" :icon="option.icon"
                            :class="option.value === 'CANCELED' ? 'p-button-danger' : 'p-button-success'"
                            :loading="state.loading.manualStatus" :disabled="!manualStatusAvailable"
                            @click="openManualStatusDialog(option)" />
                    </div>

                    <span v-if="!manualStatusAvailable" class="data-detail-hint is-warn">
                        {{ state.manualStatusAvailability.block_reason }}
                        Liberação prevista para
                        {{ formatDateOrDash(state.manualStatusAvailability.available_at) }}.
                    </span>
                    <span v-else class="data-detail-hint">
                        Disponível após 1 hora. Exige justificativa e uma confirmação adicional;
                        acontece uma única vez e o gateway não sobrescreve mais o status.
                    </span>
                </div>

                <div class="data-detail-section">
                    <span class="data-detail-section-title">Histórico da transação</span>
                    <DataTable class="data-table data-table-flush" lazy :value="state.transactionHistories">

                        <template #empty>
                            <div class="data-empty">
                                <span>Nenhum histórico registrado.</span>
                            </div>
                        </template>

                        <Column field="status" header="Status">
                            <template #body="slotProps">
                                <span class="pill pill-status" :class="statusTone(slotProps.data.status)">
                                    <span class="pill-dot"></span>
                                    {{ statusLabels[slotProps.data.status] }}
                                </span>
                            </template>
                        </Column>

                        <Column field="created_at" header="Atualizado em">
                            <template #body="slotProps">
                                <span class="cell-date">{{ formatDateOrDash(slotProps.data.created_at) }}</span>
                            </template>
                        </Column>

                        <Column field="observation" header="Observação">
                            <template #body="slotProps">
                                <span v-if="slotProps.data.observation">{{ slotProps.data.observation }}</span>
                                <span v-else class="empty-value">—</span>
                            </template>
                        </Column>

                    </DataTable>
                </div>

                <!-- notificações que o hub envia ao callback_url do cliente: uma por mudança de
                     status, com cada tentativa na linha expandida -->
                <div class="data-detail-section">
                    <span class="data-detail-section-title">Notificações enviadas ao cliente</span>

                    <!-- emissão avulsa: existe para o desfecho de recusa que nunca chegou a ser
                         comunicado, quando não há notificação nenhuma para reenviar. Quem decide
                         se a ação cabe neste status é o backend (`applicable`) -->
                    <div v-if="state.manualNotification.applicable" class="data-detail-actions">
                        <Button label="Emitir notificação do status atual" icon="pi pi-send"
                            class="p-button-outlined" :loading="state.loading.emitNotification"
                            :disabled="!state.manualNotification.available"
                            v-tooltip.right="{ value: state.manualNotification.block_reason
                                || 'Enviar ao cliente o status atual desta transação' }"
                            @click="state.emitNotification.visible = true" />
                    </div>

                    <span v-if="state.manualNotification.applicable && state.manualNotification.block_reason"
                        class="data-detail-hint is-warn">
                        {{ state.manualNotification.block_reason }}
                    </span>

                    <DataTable class="data-table data-table-flush" lazy dataKey="id" responsiveLayout="scroll"
                        v-model:expandedRows="state.expandedNotifications" :value="state.clientNotifications">

                        <template #empty>
                            <div class="data-empty">
                                <span>Nenhuma notificação enviada.</span>
                            </div>
                        </template>

                        <Column :expander="true" headerStyle="width: 3rem" />

                        <Column field="status" header="Entrega">
                            <template #body="slotProps">
                                <span class="pill pill-status" :class="deliveryTone(slotProps.data.status)">
                                    <span class="pill-dot"></span>
                                    {{ webhookDeliveryStatusLabels[slotProps.data.status] }}
                                </span>
                            </template>
                        </Column>

                        <Column field="transaction_status" header="Status enviado">
                            <template #body="slotProps">
                                <span>{{ statusLabels[slotProps.data.transaction_status] || '—' }}</span>
                            </template>
                        </Column>

                        <Column field="attempts" header="Tentativas">
                            <template #body="slotProps">
                                <span>{{ attemptsLabel(slotProps.data) }}</span>
                            </template>
                        </Column>

                        <Column field="created_at" header="Criada em">
                            <template #body="slotProps">
                                <span class="cell-date">{{ formatDateOrDash(slotProps.data.created_at) }}</span>
                            </template>
                        </Column>

                        <Column header="Reenvio">
                            <template #body="slotProps">
                                <Button v-if="!['DELIVERED', 'SUPERSEDED'].includes(slotProps.data.status)"
                                    class="p-button-outlined p-button-sm" type="button" icon="pi pi-refresh"
                                    label="Reenviar" :disabled="!slotProps.data.can_resend"
                                    :loading="state.loading.resend === slotProps.data.id"
                                    v-tooltip.left="{ value: slotProps.data.resend_block_reason || 'Enviar a notificação novamente' }"
                                    @click="resendNotification(slotProps.data)" />
                                <span v-else class="empty-value">—</span>
                            </template>
                        </Column>

                        <template #expansion="slotProps">
                            <div class="webhook-expansion">
                                <div class="data-detail-grid">
                                    <div class="data-detail-item">
                                        <span class="data-detail-label">Origem</span>
                                        <span class="data-detail-value">
                                            {{ webhookDeliveryOriginLabels[slotProps.data.origin] || '—' }}
                                        </span>
                                    </div>

                                    <div class="data-detail-item">
                                        <span class="data-detail-label">Callback URL</span>
                                        <span class="data-detail-value mono">{{ slotProps.data.callback_url }}</span>
                                    </div>

                                    <div class="data-detail-item">
                                        <span class="data-detail-label">Próxima tentativa automática</span>
                                        <span class="data-detail-value">
                                            {{ formatDateOrDash(slotProps.data.next_attempt_at) }}
                                        </span>
                                    </div>

                                    <div class="data-detail-item">
                                        <span class="data-detail-label">Entregue em</span>
                                        <span class="data-detail-value">
                                            {{ formatDateOrDash(slotProps.data.delivered_at) }}
                                        </span>
                                    </div>
                                </div>

                                <span class="data-detail-label">Payload enviado</span>
                                <pre class="webhook-payload">{{ formatPayload(slotProps.data.payload) }}</pre>

                                <span v-if="!slotProps.data.can_resend && slotProps.data.status !== 'DELIVERED'"
                                    class="data-detail-hint is-warn">
                                    {{ slotProps.data.resend_block_reason }}
                                </span>

                                <DataTable class="data-table data-table-flush webhook-attempts" lazy
                                    responsiveLayout="scroll" :value="slotProps.data.attempt_history">

                                    <template #empty>
                                        <div class="data-empty">
                                            <span>Nenhuma tentativa registrada.</span>
                                        </div>
                                    </template>

                                    <Column field="attempt" header="#" />

                                    <Column field="kind" header="Tipo">
                                        <template #body="attempt">
                                            <span>{{ webhookAttemptKindLabels[attempt.data.kind] }}</span>
                                            <span v-if="attempt.data.requested_by" class="attempt-author">
                                                {{ attempt.data.requested_by }}
                                            </span>
                                        </template>
                                    </Column>

                                    <Column field="outcome" header="Resultado">
                                        <template #body="attempt">
                                            <span class="pill pill-status"
                                                :class="attempt.data.outcome === 'SUCCESS' ? 'is-on' : 'is-off'">
                                                <span class="pill-dot"></span>
                                                {{ webhookAttemptOutcomeLabels[attempt.data.outcome] }}
                                            </span>
                                        </template>
                                    </Column>

                                    <Column field="http_status" header="HTTP">
                                        <template #body="attempt">
                                            <span>{{ attempt.data.http_status || '—' }}</span>
                                        </template>
                                    </Column>

                                    <Column field="duration_ms" header="Tempo">
                                        <template #body="attempt">
                                            <span>{{ formatDuration(attempt.data.duration_ms) }}</span>
                                        </template>
                                    </Column>

                                    <Column field="sent_at" header="Enviada em">
                                        <template #body="attempt">
                                            <span class="cell-date">{{ formatDateOrDash(attempt.data.sent_at) }}</span>
                                        </template>
                                    </Column>

                                    <Column field="error_message" header="Erro / resposta">
                                        <template #body="attempt">
                                            <span v-if="attempt.data.error_message">{{ attempt.data.error_message }}</span>
                                            <span v-else-if="attempt.data.response_body" class="mono">
                                                {{ attempt.data.response_body }}
                                            </span>
                                            <span v-else class="empty-value">—</span>
                                        </template>
                                    </Column>

                                </DataTable>
                            </div>
                        </template>

                    </DataTable>
                </div>

                <!-- tudo que os fornecedores postaram nas rotas de webhook desta transação -->
                <div class="data-detail-section">
                    <span class="data-detail-section-title">Webhooks recebidos do fornecedor</span>

                    <DataTable class="data-table data-table-flush" lazy dataKey="id" responsiveLayout="scroll"
                        v-model:expandedRows="state.expandedVendorWebhooks" :value="state.vendorWebhooks">

                        <template #empty>
                            <div class="data-empty">
                                <span>Nenhum webhook recebido.</span>
                            </div>
                        </template>

                        <Column :expander="true" headerStyle="width: 3rem" />

                        <Column field="received_at" header="Recebido em">
                            <template #body="slotProps">
                                <span class="cell-date">{{ formatDateOrDash(slotProps.data.received_at) }}</span>
                            </template>
                        </Column>

                        <Column field="vendor_type" header="Fornecedor">
                            <template #body="slotProps">
                                <span>{{ slotProps.data.vendor_type || '—' }}</span>
                            </template>
                        </Column>

                        <Column field="result" header="Resultado">
                            <template #body="slotProps">
                                <span class="pill pill-status" :class="vendorResultTone(slotProps.data.result)">
                                    <span class="pill-dot"></span>
                                    {{ vendorWebhookResultLabels[slotProps.data.result] }}
                                </span>
                            </template>
                        </Column>

                        <Column field="http_status" header="Resposta">
                            <template #body="slotProps">
                                <span>{{ slotProps.data.http_status || '—' }}</span>
                            </template>
                        </Column>

                        <Column field="duration_ms" header="Processamento">
                            <template #body="slotProps">
                                <span>{{ formatDuration(slotProps.data.duration_ms) }}</span>
                            </template>
                        </Column>

                        <template #expansion="slotProps">
                            <div class="webhook-expansion">
                                <div class="data-detail-grid">
                                    <div class="data-detail-item">
                                        <span class="data-detail-label">Rota</span>
                                        <span class="data-detail-value mono">{{ slotProps.data.endpoint }}</span>
                                    </div>

                                    <div class="data-detail-item">
                                        <span class="data-detail-label">IP de origem</span>
                                        <span class="data-detail-value mono">{{ slotProps.data.ip || '—' }}</span>
                                    </div>
                                </div>

                                <span v-if="slotProps.data.result_detail" class="data-detail-hint">
                                    {{ slotProps.data.result_detail }}
                                </span>

                                <span class="data-detail-label">Payload recebido</span>
                                <pre class="webhook-payload">{{ formatPayload(slotProps.data.payload) }}</pre>

                                <span class="data-detail-label">Cabeçalhos</span>
                                <pre class="webhook-payload">{{ formatPayload(slotProps.data.headers) }}</pre>
                            </div>
                        </template>

                    </DataTable>
                </div>

            </div>

            <template #footer>
                <Button type="button" label="Fechar" @click="handleClose" />
            </template>

        </Dialog>

        <!-- a emissão faz a plataforma processar o desfecho (nas recusas, devolver o valor ao
             jogador): confirma antes, como a alteração manual de status já faz -->
        <Dialog v-model:visible="state.emitNotification.visible" modal :draggable="false"
            header="Emitir notificação ao cliente" :style="{ width: '32rem' }">

            <p class="data-detail-hint">
                O cliente vai receber o status
                <strong>{{ statusLabels[state.modalTransaction.status] }}</strong>
                do pagamento #{{ state.modalTransaction.id }} e processar esse desfecho do lado
                dele — nas recusas, devolvendo o valor ao jogador. A emissão fica registrada com
                o seu usuário.
            </p>

            <template #footer>
                <Button type="button" label="Cancelar" class="p-button-text"
                    :disabled="state.loading.emitNotification"
                    @click="state.emitNotification.visible = false" />
                <Button type="button" label="Emitir notificação" icon="pi pi-send"
                    :loading="state.loading.emitNotification" @click="emitClientNotification" />
            </template>

        </Dialog>

        <ConfirmStatusChangeDialog v-model:visible="state.manualStatus.visible"
            v-model:observation="state.manualStatus.observation" :transactionId="state.modalTransaction.id"
            :currentStatusLabel="statusLabels[state.modalTransaction.status]"
            :targetStatus="state.manualStatus.target.value" :targetStatusLabel="state.manualStatus.target.name"
            :loading="state.loading.manualStatus" @confirm="confirmManualStatus" />
    </div>
</template>

<script>

import Button from 'primevue/button'
import Dropdown from 'primevue/dropdown'
import InputText from 'primevue/inputtext'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Dialog from 'primevue/dialog'
import MultiSelect from 'primevue/multiselect'
import { computed, reactive } from 'vue'
import services from '../../services'
import { useStore } from 'vuex'
import useNotificationToast from '../../composables/useNotificationToast'
import ProgressSpinner from 'primevue/progressspinner'
import CalendarForm from '../../components/CalendarForm.vue'
import ConfirmStatusChangeDialog from '../../components/ConfirmStatusChangeDialog.vue'
import PageHeader from '../../components/PageHeader.vue'
import TableResults from '../../components/TableResults.vue'
import useCalendarFilter from '../../composables/useCalendarFilter'
import useClients from '../../composables/useClients'
import useVendors from '../../composables/useVendors'
import useEnumLabels from '../../composables/useEnumLabels'
import moment from 'moment'

// verde entra dinheiro, vermelho sai; retirada e manual ficam neutros
const TYPE_TONES = {
    DEPOSIT: 'is-on',
    WITHDRAW: 'is-off',
}

// cor da pill da entrega da notificação e do resultado do webhook recebido
const DELIVERY_TONES = {
    DELIVERED: 'is-on',
    FAILED: 'is-off',
    SENDING: 'is-info',
    PENDING: 'is-warn',
    // descarte proposital, não falha de entrega
    SUPERSEDED: 'is-info',
}

const VENDOR_RESULT_TONES = {
    APPLIED: 'is-on',
    ERROR: 'is-off',
    IGNORED: 'is-info',
}

// cor da pill de cada status da transação
const STATUS_TONES = {
    PAID: 'is-on',
    PENDING: 'is-warn',
    PENDING_REFUNDED: 'is-warn',
    REFUNDED: 'is-info',
    CANCELED: 'is-off',
    REFUSED: 'is-off',
    EXPIRED: 'is-neutral',
}

export default {

    props: {
        player: {
            type: Object,
            default: null
        },
    },

    components: {
        Button,
        Dropdown,
        InputText,
        DataTable,
        Column,
        Dialog,
        MultiSelect,
        ProgressSpinner,
        CalendarForm,
        ConfirmStatusChangeDialog,
        PageHeader,
        TableResults,
    },

    setup(props) {

        const state = reactive({
            filter: {
                id: '',
                status: '',
                type: '',
                clients: [],
                vendors: [],
                value: null,
            },

            transactions: [],
            transaction: {},
            modalTransaction: {},
            player: {},
            clientName: '',
            vendorName: '',

            transactionHistories: [],

            // histórico de webhooks: entregas ao cliente e eventos recebidos dos fornecedores
            clientNotifications: [],
            vendorWebhooks: [],
            expandedNotifications: [],
            expandedVendorWebhooks: [],

            isDetailsModalOpen: false,
            showFilters: false,

            loading: {
                transactions: false,
                transactionsDetails: false,
                metagratorApproval: false,
                manualStatus: false,
                resend: null,
                emitNotification: false
            },

            // confirmação da emissão avulsa
            emitNotification: {
                visible: false
            },

            // emissão avulsa da notificação: o backend diz se está liberada e, se não, por quê
            manualNotification: {
                available: false,
                applicable: false,
                block_reason: ''
            },

            // alteração manual do status: alvo escolhido e justificativa do operador
            manualStatus: {
                visible: false,
                target: {},
                observation: ''
            },

            manualStatusAvailability: {
                available: false,
                available_at: null,
                block_reason: ''
            },

            metagratorApproval: {
                visible: false,
                waiting: false,
                status: '',
                error: '',
                done: false
            },

            currentPage: 1,
            perPage: 20,
            totalRecords: 0,
            first: 0,

        })

        const {
            typesLabels,
            statusLabels,
            typesOptions,
            statusOptions,
            manualStatusOptions,
            webhookDeliveryStatusLabels,
            webhookDeliveryOriginLabels,
            webhookAttemptKindLabels,
            webhookAttemptOutcomeLabels,
            vendorWebhookResultLabels,
            pixKeyTypesLabels
        } = useEnumLabels()
        const { stateCalendarFilter } = useCalendarFilter(applyFilters)

        const store = useStore()
        const { success, error, warn } = useNotificationToast()

        const { stateClients, loadClientsWithoutPagination } = useClients()
        loadClientsWithoutPagination()

        const { stateVendors, loadVendorsWithoutPagination } = useVendors()
        loadVendorsWithoutPagination()

        loadTransactions()

        function loadTransactions() {

            state.loading.transactions = true

            const params = {
                filter: {
                    ...state.filter,
                    // o campo de valor devolve null quando vazio; a query espera string
                    value: state.filter.value ?? '',
                    ...stateCalendarFilter
                },
                page: state.currentPage,
                perPage: state.perPage,
                player: props.player
            }

            const token = store.getters.getToken

            services.transactions.getAll({ params, token })
                .then((response) => {
                    state.transactions = response.data.data
                    state.currentPage = response.data.meta.current_page
                    state.totalRecords = response.data.meta.total
                })
                .catch(() => {
                    error("Não foi possível carregar as transações.")
                })
                .finally(() => {
                    state.loading.transactions = false
                })

        }

        // toda mudança de filtro volta para a primeira página
        function applyFilters() {
            state.currentPage = 1
            state.first = 0
            loadTransactions()
        }

        const periodLabel = computed(() => {
            const { begin, end } = stateCalendarFilter.date

            if (!begin && !end) return null

            const format = (date) => moment(date, 'YYYY/MM/DD').format('DD/MM/YYYY')

            if (begin && end && begin !== end) return `${format(begin)} a ${format(end)}`

            return format(begin || end)
        })

        // máscara de moeda preenchida da direita para a esquerda: 1 → 0,01; 12 → 0,12; 123 → 1,23
        const valueFilterDisplay = computed(() => {
            return state.filter.value === null || state.filter.value === '' ? '' : formatAmountToCurrency(state.filter.value)
        })

        function onValueFilterInput(event) {

            const digits = (event.target.value || '').replace(/\D/g, '')

            state.filter.value = digits ? Number(digits) / 100 : null

            // reescreve o campo: teclas inválidas não mudam o valor e ficariam na tela
            event.target.value = valueFilterDisplay.value

        }

        // até dois itens o chip mostra os nomes; acima disso o nome de todos não caberia
        function selectionLabel(items, plural) {
            if (items.length <= 2) return items.map((item) => item.name).join(', ')

            return `${items.length} ${plural}`
        }

        const activeFilters = computed(() => {
            const chips = []

            if (state.filter.id) chips.push({ key: 'id', label: 'ID', value: state.filter.id })
            if (state.filter.status) chips.push({ key: 'status', label: 'Status', value: state.filter.status.name })
            if (state.filter.type) chips.push({ key: 'type', label: 'Tipo', value: state.filter.type.name })
            if (state.filter.clients.length) chips.push({ key: 'clients', label: 'Cliente', value: selectionLabel(state.filter.clients, 'clientes') })
            if (state.filter.vendors.length) chips.push({ key: 'vendors', label: 'Fornecedor', value: selectionLabel(state.filter.vendors, 'fornecedores') })
            if (state.filter.value) chips.push({ key: 'value', label: 'Valor', value: formatAmountToCurrency(state.filter.value) })
            if (periodLabel.value) chips.push({ key: 'period', label: 'Período', value: periodLabel.value })

            return chips
        })

        const summaryText = computed(() => {
            const total = state.totalRecords
            const parts = [`${total} ${total === 1 ? 'transação encontrada' : 'transações encontradas'}`]

            if (activeFilters.value.length) {
                parts.push(`${activeFilters.value.length} ${activeFilters.value.length === 1 ? 'filtro aplicado' : 'filtros aplicados'}`)
            }

            return parts.join(' · ')
        })

        function clearPeriodFilter() {
            stateCalendarFilter.datesInterval = null
            stateCalendarFilter.date.begin = ''
            stateCalendarFilter.date.end = ''
        }

        function clearFilter(key) {

            if (key === 'period') {
                clearPeriodFilter()
            } else if (key === 'clients' || key === 'vendors') {
                state.filter[key] = []
            } else if (key === 'status' || key === 'type' || key === 'value') {
                state.filter[key] = null
            } else {
                state.filter[key] = ''
            }

            applyFilters()
        }

        function clearAllFilters() {
            state.filter.id = ''
            state.filter.status = null
            state.filter.type = null
            state.filter.clients = []
            state.filter.vendors = []
            state.filter.value = null
            clearPeriodFilter()

            applyFilters()
        }

        function statusTone(status) {
            return STATUS_TONES[status] || 'is-neutral'
        }

        function typeTone(type) {
            return TYPE_TONES[type] || 'is-neutral'
        }

        function sumField(field) {
            return state.transactions.reduce((a, b) => a + (parseFloat(b[field]) || 0), 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }).replace('R$', '')
        }

        function openDetailsModal(transaction) {

            state.isDetailsModalOpen = true

            // limpa o que ficou da transação anterior enquanto a nova não chega
            state.modalTransaction = {}
            state.player = null
            state.transactionHistories = []

            state.clientName = transaction.clientName
            state.vendorName = transaction.vendorName
            state.clientNotifications = []
            state.vendorWebhooks = []
            state.expandedNotifications = []
            state.expandedVendorWebhooks = []
            state.metagratorApproval = { visible: false, waiting: false, status: '', error: '', done: false }
            state.manualStatus = { visible: false, target: {}, observation: '' }
            state.manualStatusAvailability = { available: false, available_at: null, block_reason: '' }
            state.manualNotification = { available: false, applicable: false, block_reason: '' }
            state.emitNotification = { visible: false }

            loadTransactionDetails(transaction.id)
        }

        function loadTransactionDetails(id) {

            state.loading.transactionsDetails = true

            const token = store.getters.getToken

            return services.transactions.getByIdWithTransactionHistories({ id, token }).then((response) => {
                state.modalTransaction = response.data.transaction
                state.player = response.data.player
                state.transactionHistories = response.data.history
                state.clientNotifications = response.data.client_notifications || []
                state.vendorWebhooks = response.data.vendor_webhooks || []
                state.manualStatusAvailability = response.data.manual_status || {
                    available: false,
                    available_at: null,
                    block_reason: 'A disponibilidade da alteração manual não pôde ser confirmada.'
                }
                state.manualNotification = response.data.manual_notification || {
                    available: false,
                    applicable: false,
                    block_reason: 'A disponibilidade da emissão manual não pôde ser confirmada.'
                }

                loadMetagratorApproval(id, response.data.transaction, token)
            })
                .catch(() => {
                    error("Não foi possível carregar os detalhes das transações.")
                    state.isDetailsModalOpen = false
                })
                .finally(() => {
                    state.loading.transactionsDetails = false
                })
        }

        // Saque do Metagrator pode estar parado esperando aprovação manual no fornecedor
        function loadMetagratorApproval(id, transaction, token) {

            const isWithdraw = transaction.type === 'WITHDRAW' || transaction.type === 'REMOVAL'

            if (transaction.vendor_type !== 'METAGRATOR' || !isWithdraw) return

            if (transaction.status !== 'PENDING') return

            // As ações aparecem para todo saque PENDENTE do Metagrator. O status que o
            // fornecedor reporta é informativo: se ele não aceitar a ação, devolve o erro.
            state.metagratorApproval = { visible: true, waiting: true, status: '', error: '', done: false }

            services.transactions.getMetagratorStatus({ id, token })
                .then((response) => {
                    state.metagratorApproval = {
                        visible: true,
                        waiting: response.data.waiting_approval,
                        status: response.data.metagrator_status,
                        error: '',
                        done: false
                    }
                })
                .catch((e) => {
                    state.metagratorApproval.error = e.response && e.response.data
                        ? e.response.data.message
                        : 'fornecedor não respondeu'
                })
        }

        function handleMetagratorApproval(action) {

            state.loading.metagratorApproval = true

            const token = store.getters.getToken
            const id = state.modalTransaction.id

            services.transactions.approveMetagratorWithdraw({ id, action, token })
                .then(() => {
                    success(action === 'proceed'
                        ? 'Saque aprovado no fornecedor!'
                        : 'Saque reprovado no fornecedor!')

                    state.metagratorApproval.done = true
                    loadTransactions()
                })
                .catch((e) => {
                    error(e.response && e.response.data
                        ? e.response.data.message
                        : 'Não foi possível concluir a operação.')
                })
                .finally(() => { state.loading.metagratorApproval = false })
        }

        // só pagamento pendente pode ser resolvido à mão; o backend recusa o resto com 409.
        // pagamento já resolvido não exibe a seção: o desfecho está no histórico
        const manualStatusSectionVisible = computed(() => state.modalTransaction.status === 'PENDING')
        const manualStatusAvailable = computed(() =>
            manualStatusSectionVisible.value && state.manualStatusAvailability.available === true
        )

        function openManualStatusDialog(option) {
            state.manualStatus = { visible: true, target: option, observation: '' }
        }

        function confirmManualStatus() {

            const justification = state.manualStatus.observation.trim()
            if (justification.length < 10) {
                return error('Informe uma justificativa com pelo menos 10 caracteres.')
            }

            state.loading.manualStatus = true

            const token = store.getters.getToken
            const id = state.modalTransaction.id
            const target = state.manualStatus.target

            services.transactions.updateStatusManually({ id, status: target.value, observation: justification, token })
                .then((response) => {
                    state.manualStatus.visible = false

                    if (response.data.client_notified === false) {
                        warn('Status alterado, mas a plataforma não confirmou o callback.')
                    } else {
                        success(`Pagamento #${id} marcado como ${target.name}!`)
                    }

                    // histórico e trava vêm do backend: recarrega em vez de adivinhar o estado
                    loadTransactionDetails(id)
                    loadTransactions()
                })
                .catch((e) => {
                    error(e.response && e.response.data
                        ? e.response.data.message
                        : 'Não foi possível alterar o status do pagamento.')
                })
                .finally(() => { state.loading.manualStatus = false })
        }

        // o reenvio só é liberado pelo backend depois das tentativas automáticas, um envio por
        // vez e respeitando o intervalo mínimo; o 409 volta com o motivo em pt-BR
        function resendNotification(notification) {

            state.loading.resend = notification.id

            const token = store.getters.getToken
            const id = state.modalTransaction.id

            services.transactions.resendClientNotification({ id: notification.id, token })
                .then((response) => {
                    if (response.data.delivered) {
                        success('Notificação reenviada e confirmada pela plataforma!')
                    } else {
                        warn(`A plataforma não confirmou o reenvio: ${response.data.error_message || response.data.http_status}`)
                    }

                    // tentativas e travas vêm do backend: recarrega em vez de adivinhar o estado
                    loadTransactionDetails(id)
                })
                .catch((e) => {
                    error(e.response && e.response.data
                        ? e.response.data.message
                        : 'Não foi possível reenviar a notificação.')
                })
                .finally(() => { state.loading.resend = null })
        }

        // emissão avulsa do status atual: o backend recusa com 409/422 e o motivo em pt-BR quando
        // o status não é notificável à mão ou já tem notificação
        function emitClientNotification() {

            state.loading.emitNotification = true

            const token = store.getters.getToken
            const id = state.modalTransaction.id

            services.transactions.emitClientNotification({ id, token })
                .then((response) => {
                    state.emitNotification.visible = false

                    if (response.data.delivered) {
                        success('Notificação emitida e confirmada pela plataforma!')
                    } else {
                        warn(`A plataforma não confirmou a notificação: ${response.data.error_message || response.data.http_status}`)
                    }

                    // a notificação nova, as tentativas e a nova trava vêm do backend. A espera
                    // pela plataforma chega a meio minuto: se o operador já abriu outra transação,
                    // recarregar aqui sobrescreveria o modal que está na tela
                    if (state.modalTransaction.id === id) loadTransactionDetails(id)

                    // o selo de notificação da grade muda com a emissão
                    loadTransactions()
                })
                .catch((e) => {
                    error(e.response && e.response.data
                        ? e.response.data.message
                        : 'Não foi possível emitir a notificação.')

                    // 409/422 mudam a leitura do backend sobre a disponibilidade
                    if (state.modalTransaction.id === id) loadTransactionDetails(id)
                })
                .finally(() => { state.loading.emitNotification = false })
        }

        // o reenvio manual continua contando tentativa, então o total passa do limite automático
        function attemptsLabel(notification) {
            const { attempts, max_attempts: maxAttempts } = notification

            if (attempts > maxAttempts) return `${attempts} (limite automático: ${maxAttempts})`

            return `${attempts} de ${maxAttempts}`
        }

        function deliveryTone(status) {
            return DELIVERY_TONES[status] || 'is-neutral'
        }

        function vendorResultTone(result) {
            return VENDOR_RESULT_TONES[result] || 'is-neutral'
        }

        function formatDuration(durationMs) {
            if (durationMs === null || durationMs === undefined) return '—'

            return durationMs < 1000 ? `${durationMs} ms` : `${(durationMs / 1000).toFixed(2)} s`
        }

        // payload e cabeçalhos chegam como texto JSON; indentar deixa a leitura possível
        function formatPayload(payload) {
            if (!payload) return '—'

            try {
                return JSON.stringify(JSON.parse(payload), null, 2)
            } catch (e) {
                return payload
            }
        }

        function handleClose() {
            state.isDetailsModalOpen = false
        }

        function onPage(event) {
            state.currentPage = event.page + 1
            state.perPage = event.rows
            state.first = event.first
            loadTransactions()
        }

        function formatDate(dateString) {

            const date = moment.utc(dateString).format('DD/MM/YYYY HH:mm:ss')

            return date

        }

        // paid_at vem inválido quando a transação ainda não foi paga
        function formatDateOrDash(dateString) {

            if (!dateString) return '—'

            const date = moment.utc(dateString)

            return date.isValid() ? date.format('DD/MM/YYYY HH:mm:ss') : '—'

        }

        const isFallback = computed(() => {
            const fallback = state.modalTransaction.fallback
            return fallback === true || fallback == 1
        })

        function formatQRCode(value) {
            return value.includes('data:image/png;base64,') ? value : 'data:image/png;base64,' + value
        }

        function changeStatusBoleanToText(status) {
            return status == 'BLOCKED' ? 'Bloqueado' : 'Ativo'
        }

        function formatAmountToCurrency(amount) {
            if (!amount) amount = 0
            return parseFloat(amount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        }

        function checkIfTransactionTypeIs(type, transaction) {
            return transaction.type === type ? true : false
        }


        return {
            state,
            typesLabels,
            statusLabels,
            typesOptions,
            statusOptions,
            webhookDeliveryStatusLabels,
            webhookDeliveryOriginLabels,
            webhookAttemptKindLabels,
            webhookAttemptOutcomeLabels,
            vendorWebhookResultLabels,
            pixKeyTypesLabels,
            stateClients,
            stateVendors,
            stateCalendarFilter,
            valueFilterDisplay,
            onValueFilterInput,
            activeFilters,
            summaryText,
            applyFilters,
            clearFilter,
            clearAllFilters,
            statusTone,
            typeTone,
            loadTransactions,
            sumField,
            openDetailsModal,
            handleClose,
            onPage,
            formatDate,
            formatDateOrDash,
            isFallback,
            formatQRCode,
            changeStatusBoleanToText,
            formatAmountToCurrency,
            checkIfTransactionTypeIs,
            handleMetagratorApproval,
            manualStatusSectionVisible,
            manualStatusAvailable,
            manualStatusOptions,
            openManualStatusDialog,
            confirmManualStatus,
            resendNotification,
            emitClientNotification,
            attemptsLabel,
            deliveryTone,
            vendorResultTone,
            formatDuration,
            formatPayload
        }

    },
}
</script>
